import type { FieldIssue } from '@/composables/useFieldValidation'

const JSON_SPACE = new Set([' ', '\t', '\n', '\r'])
const DIGIT = /^[0-9]$/
const HEX = /^[0-9a-fA-F]$/
const ESCAPES = new Set(['"', '\\', '/', 'b', 'f', 'n', 'r', 't'])

/**
 * Where text stops being JSON: the offset of the first character no JSON could hold there, or the text's length when
 * it ends early. Null when it finds nothing wrong. Read from the text itself, never from a parser's message: engines
 * word that differently, V8's "Unexpected token" names no position at all, and it quotes the person's own text.
 */
function jsonFaultOffset(text: string): number | null {
    let at = 0
    const char = (): string => text.charAt(at)
    const skipSpace = (): void => {
        while (at < text.length && JSON_SPACE.has(char())) at++
    }
    const digits = (): boolean => {
        const start = at
        while (DIGIT.test(char())) at++
        return at > start
    }
    const word = (expected: string): boolean => {
        for (const letter of expected) {
            if (char() !== letter) return false
            at++
        }
        return true
    }
    const string = (): boolean => {
        at++
        while (at < text.length) {
            const next = char()
            if (next === '"') {
                at++
                return true
            }
            if (next < ' ') return false
            at++
            if (next !== '\\') continue
            if (char() === 'u') {
                at++
                for (let count = 0; count < 4; count++) {
                    if (!HEX.test(char())) return false
                    at++
                }
            } else if (ESCAPES.has(char())) {
                at++
            } else {
                return false
            }
        }
        return false
    }
    const number = (): boolean => {
        if (char() === '-') at++
        if (char() === '0') at++
        else if (!digits()) return false
        if (char() === '.') {
            at++
            if (!digits()) return false
        }
        if (char() === 'e' || char() === 'E') {
            at++
            if (char() === '+' || char() === '-') at++
            if (!digits()) return false
        }
        return true
    }
    const list = (close: string, item: () => boolean): boolean => {
        at++
        skipSpace()
        if (char() === close) {
            at++
            return true
        }
        for (;;) {
            if (!item()) return false
            skipSpace()
            if (char() === close) {
                at++
                return true
            }
            if (char() !== ',') return false
            at++
        }
    }
    const member = (): boolean => {
        skipSpace()
        if (char() !== '"' || !string()) return false
        skipSpace()
        if (char() !== ':') return false
        at++
        return value()
    }
    const value = (): boolean => {
        skipSpace()
        const next = char()
        if (next === '{') return list('}', member)
        if (next === '[') return list(']', value)
        if (next === '"') return string()
        if (next === '-' || DIGIT.test(next)) return number()
        if (next === 't') return word('true')
        if (next === 'f') return word('false')
        if (next === 'n') return word('null')
        return false
    }
    try {
        if (!value()) return at
        skipSpace()
        return at < text.length ? at : null
    } catch {
        // Nested deeper than the stack allows: the line cannot be named.
        return null
    }
}

/**
 * The line JSON that does not parse goes wrong on, or null when it cannot be told. JSON that simply ends early names
 * the last line that has anything on it.
 */
function failedLine(text: string): number | null {
    const offset = jsonFaultOffset(text)
    if (offset === null) return null
    const upTo = offset >= text.length ? text.trimEnd().length : offset
    return text.slice(0, upTo).split('\n').length
}

/**
 * A task's Role, as its `required` select already demands (#1757): a role must be chosen whenever there is one to
 * choose. With nothing to choose at all, nothing is refused. The edit form and Create task share it.
 */
export function assigneeRoleIssue(role: unknown, offered: number): FieldIssue | null {
    if (role) return null
    if (offered > 0) return { message: 'No role is chosen.', fix: 'Choose the role each run goes to.' }
    return null
}

/**
 * The task form's Workspace JSON must parse, as Save always required (#1757): an empty field leaves the workspace
 * unset. The message names the line and the fix, never the parser's own words.
 */
export function workspaceJsonIssue(value: unknown): FieldIssue | null {
    const text = String(value ?? '')
    if (!text.trim()) return null
    try {
        JSON.parse(text)
        return null
    } catch {
        const line = failedLine(text)
        if (line === null) {
            return {
                message: 'Workspace JSON does not parse.',
                fix: 'Fix the JSON, or clear the field to leave the workspace unset.',
            }
        }
        return {
            message: `Workspace JSON does not parse at line ${line}.`,
            fix: 'Fix the JSON on that line, or clear the field to leave the workspace unset.',
        }
    }
}
